import { RoundedFilledButton } from '@/components/buttons/Buttons';
import FullScreenModal from '@/components/modals/FullScreenModal';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import AddCardDialog from './AddCardDialog';
import AddCardItem from './AddCardItem';
import styles from './add-payment-modal.module.css';
import { cn } from '@/components/ui/cn';
import DropdownInput from '@/components/inputs/DropdownInput';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { GenericResponse, showTypedModal2 } from '@/common/common_utils';

type Props = {
	visible: boolean;
	setVisible: Dispatch<SetStateAction<boolean>>;
	onCardAdded?: () => void;
};

export interface PaymentCardDto {
	Id: number;
	UserId: number;
	CardTokenId?: string;
	CardType?: string;
	IsDefault?: boolean;
	ExpMonth?: number;
	ExpYear?: number;
	Last4?: string;
	State?: string;
	CreatedAt?: string;
	UpdatedAt?: string;
}

const AddPaymentModal = ({ visible, setVisible, onCardAdded }: Props) => {
	const [selectedPayment, setSelectedPayment] = useState('Prepaid');
	//const [visibleDialog, setVisibleDialog] = useState(false);

	const paymentMethods = ['Prepaid'];

	const cardsQ = useQuery({
		queryKey: ['payment-cards'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<PaymentCardDto[]>>(
				ENDPOINTS['/api/payment-cards/GetList'], {});
			return result.data.Data;
		},
		enabled: visible,
	});

	const formatCardNumber = (last4?: string) => {
		return `•••• •••• •••• ${last4 || '****'}`;
	};

	const formatExpiry = (month?: number, year?: number) => {
		if (!month || !year) return 'N/A';
		const paddedMonth = month.toString().padStart(2, '0');
		const shortYear = year.toString().slice(-2);
		return `${paddedMonth}/${shortYear}`;
	};

	const handleCardAdded = () => {
		onCardAdded?.();
		cardsQ.refetch();
		//setVisibleDialog(false);
	};

	const handleDeleteCard = async (cardId: number) => {
		await axiosInstance.post(`${ENDPOINTS['/api/payment-cards/Delete']}`, { Id: cardId });
		cardsQ.refetch();
	};

	const handleSetDefault = async (cardId: number) => {
		await axiosInstance.post(`${ENDPOINTS['/api/payment-cards/SetDefault']}`, { Id: cardId });
		cardsQ.refetch();
		onCardAdded?.();
	};

	return (
		<FullScreenModal
			header="Add payment method"
			visible={visible}
			setVisible={setVisible}
		>
			<p>
				This payment method will be charged for activity on your Xpress
				for Business account. You will not be charged until your account
				is approved and the users on your account begin to order rides,
				meals, or other Xpress products.
			</p>

			{cardsQ.isLoading && (
				<p className="text-neutral17 my-4">Loading payment cards...</p>
			)}

			{cardsQ.isSuccess && cardsQ.data && cardsQ.data.length > 0 ? (
				<div className="space-y-3 my-4">
					{cardsQ.data.map((card) => (
						<AddCardItem
							key={card.Id}
							item={{
								id: card.Id,
								number: formatCardNumber(card.Last4),
								exp: formatExpiry(card.ExpMonth, card.ExpYear),
								cardType: card.CardType,
								isDefault: card.IsDefault,
							}}
							onDelete={handleDeleteCard}
							onSetDefault={handleSetDefault}
						/>
					))}
				</div>
			) : cardsQ.isSuccess && cardsQ.data?.length === 0 ? (
				<p className="text-neutral17 my-4">No payment cards added yet.</p>
			) : null}

			<RoundedFilledButton
				id="add-payment-button"
				label="Add Payment Method"
				icon={MdAdd}
				className={styles.addPaymentButton}
				onClick={async () => {
					const res = await showTypedModal2(AddCardDialog, { onCardAdded: handleCardAdded });
				}}
			/>
			<h2 className={cn('mb-6', styles.billingMethodTitle)}>Billing Method</h2>
			<DropdownInput
				variant="outlined"
				id="dd-paymentMethod"
				value={selectedPayment}
				onChange={(e) => setSelectedPayment(e)}
				options={paymentMethods}
				label="Billing Method"
				appendTo="default"
				panelClassName="!w-[668px]"
			/>
			<p className="mt-5 text-sm text-neutral17">
				Rides will be charged individually to your payment method. We
				offer daily and monthly billing for qualifying organizations.
			</p>
		</FullScreenModal>
	);
};

export default AddPaymentModal;
